import { useTranslations } from 'next-intl';
import {
  CommentsPaneDataHandler,
  PaneContainer,
  useComments,
} from '@mentimeter/editor-properties-pane';
import {
  useActiveCanvasAreaStore,
  useActiveSidebarPaneStore,
} from '@mentimeter/editor-state';
import { TrackingContext, type ModuleId } from '@mentimeter/http-clients';
import { type UserT } from '@mentimeter/user';
import { InteractiveCanvasTabBar } from '../InteractiveCanvasTabBar';
import { SidebarTabbedInterface } from '../SidebarTabbedInterface';
import {
  isValidPaneId,
  useHandleSetActiveSidebarTab,
  useCreateSidebarTabHandler,
  useSetPaneWithTracking,
} from '../utils';
import type { SidebarTabs } from '../types';
import { SIDEBAR_PANES } from '../SidebarComponents';

const getViewOnlySidebarDetails = (userIsCommentCollaborator: boolean) => {
  const sidebarTabSurfaces = userIsCommentCollaborator
    ? [['comments'] as SidebarTabs]
    : [[]];

  return {
    sidebarPanes: SIDEBAR_PANES,
    sidebarTabSurfaces,
  };
};

export const SideBarViewOnly = ({
  user,
  workspaceId,
  activeQuestionId,
  activeQuestionModuleId,
  activeQuestionAdminKey,
  userIsCommentCollaborator,
}: {
  user: UserT | null;
  workspaceId: number | null;
  activeQuestionId: string;
  activeQuestionModuleId: ModuleId;
  activeQuestionAdminKey: string;
  userIsCommentCollaborator: boolean;
}) => {
  const t = useTranslations('creation');
  const activeCanvasArea = useActiveCanvasAreaStore(
    (state) => state.activeCanvasArea,
  );

  const { activeComments } = useComments(
    userIsCommentCollaborator ? activeQuestionAdminKey : undefined,
  );
  const activePane = useActiveSidebarPaneStore(
    (state) => state.activeSidebarPane,
  );

  const { sidebarPanes, sidebarTabSurfaces } = getViewOnlySidebarDetails(
    userIsCommentCollaborator,
  );

  const isShared = Boolean(workspaceId);
  const currentPlan = user?.features.type || 'registered';
  const roleInWorkspace = user?.role || 'user';

  const setPaneWithTracking = useSetPaneWithTracking(
    false,
    isShared,
    currentPlan,
    roleInWorkspace,
    TrackingContext.ViewOnly,
  );

  const handleSidebarTabClick = useCreateSidebarTabHandler(
    setPaneWithTracking,
    sidebarPanes,
  );

  const handleSetActiveSidebarTab = useHandleSetActiveSidebarTab(
    setPaneWithTracking,
    sidebarPanes,
  );

  const getIconProps = {
    slideType: activeQuestionModuleId,
    canvasArea: activeCanvasArea,
    commentsCount: activeComments.length,
    isMobile: false,
  };

  return (
    <SidebarTabbedInterface
      activePane={activePane}
      handleSetActiveSidebarTab={handleSetActiveSidebarTab}
    >
      <InteractiveCanvasTabBar
        sidebarPanes={sidebarPanes}
        onClick={handleSidebarTabClick}
        getIconProps={getIconProps}
        sidebarTabSurfaces={sidebarTabSurfaces}
      />
      {activePane && isValidPaneId(activePane, sidebarPanes) && (
        <PaneContainer
          activeQuestionId={activeQuestionId}
          title={t('editor_sidebar.sidebar_comments_label')}
          onDismiss={() => setPaneWithTracking('closed')}
          compactMode={false}
        >
          {userIsCommentCollaborator && (
            <CommentsPaneDataHandler
              trackingContext={TrackingContext.ViewOnly}
              visible
            />
          )}
        </PaneContainer>
      )}
    </SidebarTabbedInterface>
  );
};
